/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { AccordionGroup, View } from '@az/starc-ui';
import { useLabels } from '@/hooks/useLabels';
import type { ContentStackFaq } from '../../interface';
import { Grid } from '@/components/Grid';
import { FaqItem } from './FaqItem/FaqItem';
import styles from './CMSFaq.module.scss';
import { CMSHeadline } from '../CMSHeadline/CMSHeadline';
import Head from 'next/head';
import { useMemo } from 'react';
import { getFAQSchema } from '@/features/common/utils/getFAQSchema';
import { useAppData } from '@/hooks/useAppData';
import React from 'react';

const labelMap = {
  frequentlyAskedQuestions: 'label_Rewards_FAQ_FrequentlyAskedQuestions',
};

type Props = {
  content: ContentStackFaq;
  pageURL?: string;
};

type WrapperProps = {
  children: React.ReactNode[];
  isGridTheme: boolean;
  questionNames: string[] | undefined;
};

const Wrapper = ({ children, isGridTheme, questionNames }: WrapperProps) => {
  const { appData } = useAppData();
  const { deviceType } = appData;
  const isABot = deviceType === 'bot';

  if (isGridTheme) {
    return (
      <Grid container spacing={0} className={styles.gridContainer}>
        {children}
      </Grid>
    );
  }

  return (
    <AccordionGroup defaultOpenItems={isABot ? questionNames : undefined} variant="list">
      {children}
    </AccordionGroup>
  );
};

export const CMSFaq = ({ content, pageURL }: Props) => {
  const { faq, theme, heading, font_size } = content;
  const { frequentlyAskedQuestions } = useLabels(labelMap);
  const faqHeading = heading || frequentlyAskedQuestions;
  const isGridTheme = theme === 'Grid View';

  const schema = useMemo(() => {
    if (!faq) {
      return null;
    }
    return JSON.stringify(getFAQSchema(faq, pageURL));
  }, [faq, pageURL]);

  const questionNames = useMemo(() => faq?.map(({ question }) => question), [faq]);

  if (!faq?.length) {
    return null;
  }

  return (
    <View>
      {schema && (
        <Head>
          <script
            key="cms-faq-schema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schema }}
          />
        </Head>
      )}
      <CMSHeadline>{faqHeading}</CMSHeadline>
      <Wrapper isGridTheme={isGridTheme} questionNames={questionNames}>
        {faq.map(({ question, answer, icon }) => (
          <FaqItem
            key={question}
            fontSize={font_size}
            question={question}
            answer={answer}
            icon={icon}
            isGridTheme={isGridTheme}
          />
        ))}
      </Wrapper>
    </View>
  );
};
